import { useMemo } from "react";
import {
  allowedPlatformFullnameMapping,
  allowedSelectCoverPlatforms,
  allowedUploadCoverPlatforms,
} from "../const";
import type { Platform } from "features/post/format-post";

const canUsePlatform = (
  selectedPlatform: string,
  allowedPlatforms: readonly string[],
  platforms: Platform[]
) => {
  const allowedPlatformCodes = allowedPlatforms as readonly string[];
  if (selectedPlatform === "LINKED") {
    return platforms.some((platform) =>
      allowedPlatformCodes.includes(platform.TYPE)
    );
  }

  return allowedPlatformCodes.includes(selectedPlatform);
};

const canUploadForPlatform = (
  selectedPlatform: string,
  platforms: Platform[]
) => {
  const selectablePlatformCodes =
    allowedSelectCoverPlatforms as readonly string[];
  const uploadablePlatformCodes =
    allowedUploadCoverPlatforms as readonly string[];

  if (selectedPlatform === "LINKED") {
    const coverRelevantPlatforms = platforms.filter((platform) =>
      selectablePlatformCodes.includes(platform.TYPE)
    );

    return (
      coverRelevantPlatforms.length > 0 &&
      coverRelevantPlatforms.every((platform) =>
        uploadablePlatformCodes.includes(platform.TYPE)
      )
    );
  }

  return uploadablePlatformCodes.includes(selectedPlatform);
};

const getAllowedPlatforms = (
  platforms: Platform[],
  allowedPlatforms: readonly string[]
) => platforms.filter((platform) => allowedPlatforms.includes(platform.TYPE));

interface UsePlatformPermissionsProps {
  selectedPlatform: string;
  platforms: Platform[];
  videoLoadingError?: boolean;
  videoUrl?: string;
}

export function usePlatformPermissions({
  selectedPlatform,
  platforms,
  videoLoadingError,
  videoUrl,
}: UsePlatformPermissionsProps) {
  const allowUpload = useMemo(() => {
    if (!videoUrl) {
      return videoLoadingError
        ? canUploadForPlatform(selectedPlatform, platforms)
        : false;
    }

    return canUploadForPlatform(selectedPlatform, platforms);
  }, [videoLoadingError, videoUrl, selectedPlatform, platforms]);

  const displayPlatforms = useMemo(
    () => getAllowedPlatforms(platforms, allowedSelectCoverPlatforms),
    [platforms]
  );

  const platformFullname = useMemo(
    () => allowedPlatformFullnameMapping[selectedPlatform] || "",
    [selectedPlatform]
  );

  const canSelectFrame = useMemo(
    () =>
      canUsePlatform(selectedPlatform, allowedSelectCoverPlatforms, platforms),
    [platforms, selectedPlatform]
  );

  return {
    allowUpload,
    displayPlatforms,
    platformFullname,
    canSelectFrame,
  };
}
